import { ColorId, ColorOption } from '@griply/types';

// `label` and `value` are both persisted identities — editing either orphans documents.
export const predefinedColorOptions: ColorOption[] = [
  { label: ColorId.ScarletFlame, value: '#FA2835', darkValue: '#FF5257' },
  { label: ColorId.StrawberrySorbet, value: '#FF6E77', darkValue: '#FF8086' },
  { label: ColorId.BlushPink, value: '#FFA8AD', darkValue: '#FFADB1' },
  { label: ColorId.CitrusEmber, value: '#FF6B1C', darkValue: '#FF8B3D' },
  { label: ColorId.SunsetCoral, value: '#FBA575', darkValue: '#FCAE7E' },
  { label: ColorId.ToastedPeach, value: '#F3BD96', darkValue: '#F6C6A2' },
  { label: ColorId.GoldenMarigold, value: '#F7B10C', darkValue: '#FAC02E' },
  { label: ColorId.SunshineGold, value: '#FBCA75', darkValue: '#FBCE74' },
  { label: ColorId.HoneyGlow, value: '#F3E396', darkValue: '#F5E79E' },
  { label: ColorId.VerdantLeaf, value: '#57AC40', darkValue: '#70D65C' },
  { label: ColorId.HerbalSage, value: '#98BD82', darkValue: '#A8D194' },
  { label: ColorId.FrostedMint, value: '#C7E0C3', darkValue: '#C3E4BE' },
  { label: ColorId.OceanTeal, value: '#07C49B', darkValue: '#30E8BD' },
  { label: ColorId.SeafoamMist, value: '#86C2B6', darkValue: '#92D3C6' },
  { label: ColorId.MintWhisper, value: '#ACE1D6', darkValue: '#B0E8DD' },
  { label: ColorId.ElectricSapphire, value: '#066DFE', darkValue: '#3392FF' },
  { label: ColorId.AzureWave, value: '#599EFF', darkValue: '#70AEFF' },
  { label: ColorId.CeruleanBreeze, value: '#9DC6FF', darkValue: '#A3C9FF' },
  { label: ColorId.RoyalAmethyst, value: '#671DD3', darkValue: '#9F63F2' },
  { label: ColorId.VividPeriwinkle, value: '#8875FB', darkValue: '#9B88FC' },
  { label: ColorId.CoolSky, value: '#96AAF3', darkValue: '#AABAF8' },
  { label: ColorId.FuchsiaRose, value: '#FF236F', darkValue: '#FF528B' },
  { label: ColorId.CottonCandy, value: '#FF98B7', darkValue: '#FF99B8' },
  { label: ColorId.PetalPink, value: '#F2C5D7', darkValue: '#F7B6CF' },
  { label: ColorId.MidnightIndigo, value: '#2C2E69', darkValue: '#7272CA' },
  { label: ColorId.DuskyLavender, value: '#6A6693', darkValue: '#A7A4CC' },
  { label: ColorId.LavenderMist, value: '#B5B2CF', darkValue: '#C5C2E0' },
  { label: ColorId.CinnamonOak, value: '#866E53', darkValue: '#C09C72' },
  { label: ColorId.AutumnTaupe, value: '#A1907E', darkValue: '#C8B39D' },
  { label: ColorId.VintageSand, value: '#C2B59D', darkValue: '#DCD1BC' },
  { label: ColorId.DeepCharcoal, value: '#2C2A24', darkValue: '#959183' },
  { label: ColorId.SteelGray, value: '#8A8A8A', darkValue: '#ADADAD' },
  { label: ColorId.SilveryCloud, value: '#C7C7C7', darkValue: '#D1D1D1' },
];

export const defaultSmartListColorOption = predefinedColorOptions.find(
  (option) => option.label === ColorId.ElectricSapphire,
)!;

export const defaultTagColorOption =
  predefinedColorOptions[predefinedColorOptions.length - 1];

// Every stored `color` is a registry key: canonical ColorId or legacy light-hex alias.
const optionByColorKey = new Map<string, ColorOption>();
predefinedColorOptions.forEach((option) => {
  optionByColorKey.set(option.label, option);
  optionByColorKey.set(option.value.toLowerCase(), option);
});

export const findColorOption = (
  color: string | null | undefined,
): ColorOption | undefined =>
  color ? optionByColorKey.get(color.toLowerCase()) : undefined;

export const getSchemeColor = <T extends string | null | undefined>(
  color: T,
  isDark: boolean,
): T => {
  const option = findColorOption(color);
  if (!option) {
    return color;
  }

  return (isDark ? option.darkValue : option.value) as T;
};
